export const productImage =
  'https://cdn.magicpatterns.com/uploads/2na94hwzFu1k14jzNs9Ces/0a003b11-f33b-47f6-a644-50bef11e05c4.webp'

// Spine colors of the six volumes, in order (matches the product box)
export const volumeColors = [
  { bg: 'bg-blue-600', fg: 'text-white' },
  { bg: 'bg-cyan-500', fg: 'text-ink' },
  { bg: 'bg-red-500', fg: 'text-white' },
  { bg: 'bg-sun', fg: 'text-ink' },
  { bg: 'bg-violet-600', fg: 'text-white' },
  { bg: 'bg-green-600', fg: 'text-white' },
]

export const guarantees = [
  'Instant Download',
  'No Subscription',
  'Lifetime Access',
  '7-Day Guarantee',
]

export const stats = [
  { value: '6', label: 'Complete Digital Volumes' },
  { value: '6', label: 'Core Career Readiness Areas' },
  { value: '5', label: 'Practical Teacher Bonuses' },
  { value: '0', label: 'Monthly Payments' },
]

export const differenceIntro = [
  'The hard part is turning broad topics like strengths, career pathways, communication, and interviews into a clear lesson students can actually participate in.',
  'CAREER READY™ brings the materials together: explanations, visual activities, printable worksheets, practical challenges, and assessment support.',
  'Less time assembling disconnected resources. More time helping students explore what comes next.',
]

export const differencePillars = [
  {
    title: '6 connected learning areas',
    text: 'From discovering personal strengths to applying skills in realistic workplace situations.',
  },
  {
    title: '6 complete digital volumes',
    text: 'Organized by topic so you can find relevant material without searching through scattered files.',
  },
  {
    title: 'Ready-to-use printable resources',
    text: 'For classroom activities, independent work, group discussions, and practical projects.',
  },
]

export const beforeItems = [
  'Hours searching for career activities and adapting materials from different websites.',
  'Worksheets that ask students to name a dream job—but do little to help them explore it.',
  'Lessons on communication and workplace skills that stay abstract.',
  'Projects that need instructions, examples, and assessment criteria you still have to create.',
  'Another Sunday evening spent deciding what to teach on Monday.',
]

export const afterItems = [
  'Choose materials from six organized career readiness volumes.',
  'Use visual worksheets, career comparisons, and pathway activities.',
  'Bring workplace skills to life through discussions and realistic scenarios.',
  'Guide practical projects with structured tasks and assessment support.',
  'Build a teaching sequence without rebuilding every resource yourself.',
]

export const recognizeItems = [
  {
    title: 'You sit down to plan “one quick lesson”… and an hour disappears.',
    text: 'You find an interesting activity, but it needs new instructions, a better worksheet, and a way to assess it.',
  },
  {
    title:
      'You want career education to feel relevant—not like another worksheet about jobs.',
    text: 'Students need opportunities to compare, discuss, solve problems, and apply what they learn.',
  },
  {
    title:
      'You’re expected to teach skills that don’t fit neatly into a textbook.',
    text: 'Teamwork. Professional communication. Interviews. Making informed choices about the future.',
  },
  {
    title:
      'Your resources are scattered across downloads, bookmarks, and folders.',
    text: 'Finding the right material can take almost as long as creating it.',
  },
  {
    title: 'Sunday night arrives, and Monday’s lesson still isn’t ready.',
    text: 'The preparation follows you home—and takes time away from everything else.',
  },
]

export const volumes = [
  {
    emoji: '📘',
    number: 'Volume 1',
    title: 'Discover Your Strengths & Interests',
    paragraphs: [
      'Help students explore their interests, recognize their strengths, and identify skills they can develop.',
      'Guided reflections, visual activities, and classroom discussions give students a practical starting point—without treating their interests as permanent labels or telling them there is only one “right” career.',
    ],
  },
  {
    emoji: '📗',
    number: 'Volume 2',
    title: 'Career Exploration & Opportunities',
    paragraphs: [
      'Move beyond lists of job titles.',
      'Explore career families, everyday work tasks, professional environments, and possible pathways through illustrated career profiles, visual maps, comparison worksheets, and research activities.',
      'Students investigate possibilities while you follow an organized set of teaching materials.',
    ],
  },
  {
    emoji: '📙',
    number: 'Volume 3',
    title: 'Communication & Workplace Skills',
    paragraphs: [
      'Turn broad ideas like “good communication” and “teamwork” into activities students can practice.',
      'Use workplace situations, group challenges, and guided discussions to explore asking questions, following instructions, sharing responsibilities, solving problems, and handling disagreements.',
    ],
  },
  {
    emoji: '📕',
    number: 'Volume 4',
    title: 'Resumes, Applications & Interviews',
    paragraphs: [
      'Make unfamiliar professional situations easier to understand.',
      'Guide students through age-appropriate resume activities, application examples, interview preparation, and role-play exercises—with structured tasks instead of a blank page and “write your resume.”',
    ],
  },
  {
    emoji: '📓',
    number: 'Volume 5',
    title: 'Financial & Professional Life Skills',
    paragraphs: [
      'Connect career choices with everyday decisions.',
      'Explore topics such as income, budgeting, workplace responsibilities, and professional decision-making through practical examples and student activities.',
      'Help students think beyond choosing a job to understanding the responsibilities that come with working life.',
    ],
  },
  {
    emoji: '📒',
    number: 'Volume 6',
    title: 'Real-World Career Projects & Simulations',
    paragraphs: [
      'Bring the learning together.',
      'Students take on roles, respond to workplace challenges, collaborate on projects, and explain their decisions through structured simulations and practical career activities.',
      'Give your lessons a clear purpose: not just learning about skills, but using them.',
    ],
  },
]

export const toolkitItems = [
  {
    emoji: '✏️',
    title: 'Printable Worksheets & Activities',
    text: 'Structured tasks with clear directions and space for students to think, compare, and respond.',
  },
  {
    emoji: '🧭',
    title: 'Visual Career Maps & Exploration Tools',
    text: 'Resources that help make career options and possible pathways easier to discuss.',
  },
  {
    emoji: '💬',
    title: 'Practical Scenarios & Role-Play Activities',
    text: 'Opportunities to practice communication, teamwork, interviews, and decision-making.',
  },
  {
    emoji: '📋',
    title: 'Teacher Guidance',
    text: 'Teaching prompts, activity instructions, and suggestions to help you introduce and guide the work.',
  },
  {
    emoji: '✅',
    title: 'Assessments & Rubrics',
    text: 'Tools to evaluate understanding, participation, research, and practical application.',
  },
  {
    emoji: '🔑',
    title: 'Answer Keys & Sample Responses',
    text: 'Answers for fixed-response tasks and examples to support the review of open-ended work.',
  },
]

export type SamplePage = {
  tab: string
  volume: string
  caption: string
  text: string
  image?: string
}

export const samplePages: SamplePage[] = [
  {
    tab: 'Overview',
    volume: 'Career Exploration',
    caption: 'The World of Careers',
    text: 'Ten career families on one visual map—a starting point for exploring endless possibilities.',
    image:
      'https://cdn.magicpatterns.com/uploads/oMXxbMKj4u9pRTPjcEyG5b/Captura-de-pantalla-2026-10-09-a-la_s_-10.01.54_p._m..webp',
  },
  {
    tab: 'Healthcare',
    volume: 'Career Family Profile',
    caption: 'Healthcare & Human Services',
    text: 'Key job tasks, essential skills, work environments, and a career challenge to solve.',
    image:
      'https://cdn.magicpatterns.com/uploads/ejYG3gWGrHapRX6yNRW5zF/Captura-de-pantalla-2026-10-09-a-la_s_-10.02.20_p._m..webp',
  },
  {
    tab: 'Tech',
    volume: 'Career Family Profile',
    caption: 'Technology & Digital Innovation',
    text: 'The six-step app development process, plus a mini challenge to design a homework app.',
    image:
      'https://cdn.magicpatterns.com/uploads/vCzdzAbAYXHuscr4PP6A7e/Captura-de-pantalla-2026-10-09-a-la_s_-10.02.59_p._m..webp',
  },
  {
    tab: 'Engineering',
    volume: 'Career Family Profile',
    caption: 'Engineering & Design',
    text: 'The design–test–improve cycle and a hands-on bridge design challenge.',
    image:
      'https://cdn.magicpatterns.com/uploads/dcTbDfJsdqaUGYjuiceQZU/Captura-de-pantalla-2026-10-09-a-la_s_-10.03.20_p._m..webp',
  },
  {
    tab: 'Trades',
    volume: 'Career Family Activity',
    caption: 'Skilled Trades & Construction',
    text: 'Match each professional to their tools and tasks, then reflect on training paths.',
    image:
      'https://cdn.magicpatterns.com/uploads/7WDS81MJAGdDSozdhvWouX/Captura-de-pantalla-2026-10-09-a-la_s_-10.03.39_p._m..webp',
  },
]

export const instantIntro =
  'Your purchase includes the six CAREER READY™ digital volumes, organized around the core topics of career readiness.'

export const instantItems = [
  'Career exploration activities, visual worksheets, workplace skills tasks, interview practice, practical projects, and assessment resources.',
  'All five teacher bonuses, including the yearly planner and emergency lesson materials.',
  'Read the PDFs on your computer, tablet, or phone, and print the pages you need for your lessons.',
]

export const instantNote =
  'These are downloadable digital resources. No physical books will be shipped.'

export const bonuses = [
  {
    title: 'Yearly Teaching Planner',
    lead: 'Turn six volumes into a clear teaching sequence.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/253e34ee-383c-488c-9f46-887476902848.jpg',
  },
  {
    title: 'Printable Career Worksheets',
    lead: 'Reusable templates for research, comparisons, and reflections.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/fc91f6a2-8fea-4eec-aa43-367a12d42f2c.jpg',
  },
  {
    title: 'Interview Role-Play Cards',
    lead: 'Ready-made roles and prompts for interview practice.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/916893a2-2ae6-4021-95eb-7a832ccd6c81.jpg',
  },
  {
    title: 'Assessment Rubrics',
    lead: 'Clear criteria for research, teamwork, and projects.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/96e1f3e9-f345-402f-94c4-56e8485f35be.jpg',
  },
  {
    title: 'Emergency Career Lessons',
    lead: 'Standalone backups for when your plan changes.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/db53623b-7b50-438e-b886-c3dfe57ed4f6.jpg',
  },
]

export const offerRows = [
  { label: 'All 6 CAREER READY™ Digital Volumes', value: 'Included' },
  {
    label: 'Career Exploration & Workplace Skills Activities',
    value: 'Included',
  },
  { label: 'Printable Worksheets & Practical Projects', value: 'Included' },
  { label: 'Assessments, Answer Keys & Sample Responses', value: 'Included' },
  { label: 'All 5 Teacher Bonuses', value: 'FREE' },
  { label: 'Lifetime Access to Your Purchased Materials', value: 'Included' },
]

export const guaranteeParagraphs = [
  'Open the volumes. Explore the worksheets. Review the activities and see how the materials fit your teaching.',
  'If the pack isn’t what you expected, contact us within seven days of purchase for a full refund.',
  'No need to commit to a monthly subscription to find out whether the resources work for you.',
]

export const imagineContrasts = [
  {
    before: 'You’re not opening ten browser tabs to piece together a lesson.',
    after: 'You’re opening the relevant volume.',
  },
  {
    before: 'You’re not starting with a blank worksheet.',
    after: 'You’re choosing an activity.',
  },
  {
    before:
      'You’re not inventing every instruction, discussion prompt, and assessment criterion.',
    after:
      'You’re reviewing prepared resources and making the adjustments your students need.',
  },
]

export const faqs = [
  {
    q: 'How do I receive the material?',
    a: [
      'After your purchase, you receive access to download the six digital volumes and all five bonuses. The materials are provided as PDFs that you can save and print.',
    ],
  },
  {
    q: 'Does this work if I’m a beginner teacher?',
    a: [
      'Yes. The organized topics, activity instructions, teaching guidance, and assessment support give you a starting point for planning and delivering lessons.',
      'You can still adapt the activities to your students, available class time, and teaching style.',
    ],
  },
  {
    q: 'What grade levels does this cover?',
    a: [
      'CAREER READY™ is designed for middle and high school educators, with the core activities primarily aimed at grades 7–9.',
      'Review the examples and activity demands when choosing materials for younger or older students.',
    ],
  },
  {
    q: 'Can I use it throughout the school year?',
    a: [
      'The six volumes cover connected career readiness topics, and the included yearly planner helps you organize them into a teaching sequence.',
      'Your pace will depend on your timetable, students, and school requirements. You can work through the topics in sequence or choose resources for specific lessons.',
    ],
  },
  {
    q: 'Are the five bonuses included in the $27 price?',
    a: [
      'Yes. All six volumes and all five bonuses are included in your one-time $27 payment. No separate bonus purchase is required.',
    ],
  },
  {
    q: 'Do I have to pay monthly?',
    a: [
      'No. CAREER READY™ is a one-time purchase, not a subscription. You receive lifetime access to the materials included in your purchase.',
    ],
  },
  {
    q: 'Will I receive printed books?',
    a: [
      'No. This is a digital product. The book mockups illustrate the collection; your purchase includes downloadable files that you can use digitally or print.',
    ],
  },
  {
    q: 'What happens if the pack isn’t right for me?',
    a: [
      'Contact us within seven days of purchase to request a refund under the 7-day money-back guarantee.',
    ],
  },
]
